import React from 'react';
import _ from 'lodash';
import { useDeviceQuery, useFirstRender } from '../../../lib';
import {
    canPlay,
    getFirstUrl as getFirstVideoUrl,
    shouldReload,
    sortVideoSources,
} from './formatters';
import { IframeProps, VideoSource } from './types';
import { VimeoIframe } from './VimeoIframe';
import { useInView } from 'react-intersection-observer';

export type VideoProps = {
    sources: VideoSource[];
    style?: React.CSSProperties;
    className?: string;
    muted?: boolean;
    play?: boolean;
    resetOnPlay?: boolean;
    autoPlay?: boolean;
    loop?: boolean;
    playsInline?: boolean;
    poster?: string;
    controls?: boolean;
    height?: string;
};

const DefaultIframe = ({ className, style, url, height }: IframeProps) => (
    <iframe
        style={style}
        className={className}
        src={
            url +
            '?cc_load_policy=0&iv_load_policy=0&modestbranding=1&showinfo=0&rel=0'
        }
        allowFullScreen
        height={height}
        frameBorder={0}
    />
);

const mediaUrl = 'https://media.alude.com.br/';

const getCdnUrl = (url: string) => {
    const parts = url.split('/');
    if (parts.length === 0) {
        return url;
    }
    const videoPath = parts[parts.length - 1];
    return mediaUrl + videoPath;
};

// eslint-disable-next-line max-lines-per-function, complexity
export const Video = ({
    sources,
    poster,
    autoPlay,
    play,
    resetOnPlay,
    muted,
    loop,
    style,
    playsInline = true,
    className,
    controls,
    height,
}: VideoProps): JSX.Element => {
    const { ref: inViewRef, inView } = useInView({
        triggerOnce: true,
        fallbackInView: true,
    });
    const ref = React.useRef<HTMLVideoElement>(null);
    const [sortedVideos, setSortedVideos] = React.useState<VideoSource[]>();
    const currentDevice = useDeviceQuery();
    const firstRender = useFirstRender();

    const [iframes, videos] = _.partition(
        sources,
        source => source.format === 'embed'
    );
    const isVimeo = iframes.length && iframes[0].url.includes('vimeo.com');
    const IframeComponent = isVimeo ? VimeoIframe : DefaultIframe;

    React.useEffect(() => {
        const lastUrl = getFirstVideoUrl(sortedVideos);
        const updatedVideos = sortVideoSources(videos, currentDevice);
        setSortedVideos(updatedVideos);

        if (
            shouldReload(
                firstRender,
                lastUrl,
                getFirstVideoUrl(updatedVideos)
            ) &&
            ref.current
        ) {
            ref.current.load();
        }
    }, [currentDevice]);

    React.useEffect(() => {
        if (autoPlay || !canPlay(ref.current)) return;
        if (play) {
            if (resetOnPlay) ref.current.currentTime = 0;
            ref.current.play().catch(() => null);
        } else {
            ref.current.pause();
        }
    }, [play, getFirstVideoUrl(sortedVideos)]);

    return iframes.length ? (
        <IframeComponent
            autoPlay={autoPlay}
            poster={poster}
            className={className}
            style={style}
            url={iframes[0].url}
            height={height}
        />
    ) : (
        <div ref={inViewRef}>
            <video
                ref={ref}
                poster={poster}
                autoPlay={autoPlay}
                loop={loop}
                muted={muted}
                controls={controls}
                style={style}
                playsInline={playsInline}
                className={className}>
                {sortedVideos &&
                    inView &&
                    sortedVideos.map(video => (
                        <source
                            key={getCdnUrl(video.url)}
                            src={getCdnUrl(video.url)}
                            type={`video/${video.format}`}
                        />
                    ))}
                Your browser does not support the video tag.
            </video>
        </div>
    );
};
